import {FC, useState} from 'react';

import {Typography, useMediaQuery} from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import {useTranslation} from 'react-i18next';

import ChevronLeftIcon from 'assets/icons/chevronLeft';
import theme from 'config/theme';
import {CurrentStoreType} from 'store/types/app';
import {CreateAddressInputType, StoreArea, TPinMapSelectedLocation, UserAddress} from 'types';
import {StyledIconButton} from 'views/address-drawer/styles';

import {AddressForm} from './AddressForm';
import {StyledAddressFormDialog, StyledAddressFormDialogContent, StyledTitleContainer} from './styles';

interface Props {
  open: boolean;
  loading: boolean;
  address?: UserAddress;
  isUserEnabledEtax?: boolean;
  onClose: () => void;
  onSubmit: (values: CreateAddressInputType, closeAddressForm?: boolean) => void;
  isBilling?: boolean;
  currentStore?: CurrentStoreType;
  openFullMapView?: boolean;
  locationsV2?: StoreArea[];
  setOpenFullMapView?: React.Dispatch<React.SetStateAction<boolean>>;
  selectedLocation: TPinMapSelectedLocation;
  hasUserMovedMapManually: boolean;
}

export const AddressFormModal: FC<Props> = ({
  open,
  loading,
  address,
  onClose,
  onSubmit,
  isUserEnabledEtax = false,
  isBilling = false,
  currentStore,
  openFullMapView = false,
  setOpenFullMapView,
  locationsV2,
  selectedLocation,
  hasUserMovedMapManually
}) => {
  const {t} = useTranslation();
  let label = address?.id ? t('label.editMyAddress') : t('label.createMyAddress');
  let titleId = '';
  const [fullMapView, setFullMapView] = useState(false);

  const updatePadding = fullMapView || openFullMapView;

  if (openFullMapView || fullMapView) {
    label = t('addressV2.selectLocation');
  } else if (address?.id && ['label.editMyAddress'].includes(label)) {
    label = t('label.editMyAddress');
  } else if (!address?.id && address?.isDefault === true && ['createMyAddress'.includes(label)]) {
    label = t('label.createMyAddress');
  } else if (!address?.id && address?.isDefault === false) {
    label = t('label.addNewAddress');
    titleId = 'lbl_title_add_delivery_address';
  }

  if (isBilling && !address?.id) {
    label = t('taxInvoice.label.billingAddress');
  }

  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));

  function renderAddressTitle() {
    return (
      <StyledTitleContainer>
        <StyledIconButton
          size="small"
          color="inherit"
          onClick={() => {
            onClose();
          }}
          aria-label="close"
        >
          <ChevronLeftIcon width={28} height={28} />
        </StyledIconButton>
        <Typography fontWeight="bold" fontSize={20} id={titleId}>
          {label}
        </Typography>
        <Typography fontSize={16} color={'#8E8E8E'}>
          2/2
        </Typography>
      </StyledTitleContainer>
    );
  }

  function renderFullMapViewTitle() {
    return (
      <StyledTitleContainer>
        <StyledIconButton
          size="small"
          color="inherit"
          onClick={() => {
            setFullMapView(false);
            setOpenFullMapView && setOpenFullMapView(false);
          }}
          aria-label="close"
        >
          <ChevronLeftIcon width={28} height={28} />
        </StyledIconButton>
        <Typography fontWeight="bold" fontSize={20}>
          {label}
        </Typography>
      </StyledTitleContainer>
    );
  }

  return (
    <StyledAddressFormDialog
      open={open}
      fullScreen={isMobile}
      onClose={() => {
        //ReactNative.toggleBackBtnOnCheckoutHeader(true);
        onClose();
        setFullMapView(false);
        setOpenFullMapView && setOpenFullMapView(false);
      }}
    >
      <DialogTitle sx={{padding: '16px 24px'}}>
        {!fullMapView && !openFullMapView && renderAddressTitle()}
        {(fullMapView || openFullMapView) && renderFullMapViewTitle()}
      </DialogTitle>
      <StyledAddressFormDialogContent updatePadding={updatePadding}>
        <AddressForm
          address={address}
          handleSubmit={(values, _formHelper, closeAddressForm) => {
            //ReactNative.toggleBackBtnOnCheckoutHeader(true);
            onSubmit(values, closeAddressForm);
          }}
          fullMapView={fullMapView || openFullMapView}
          setFullMapView={setFullMapView}
          isUserEnabledEtax={isUserEnabledEtax}
          currentStore={currentStore}
          locationsV2={locationsV2}
          selectedLocation={selectedLocation}
          loading={loading}
          hasUserMovedMapManually={hasUserMovedMapManually}
        />
      </StyledAddressFormDialogContent>
    </StyledAddressFormDialog>
  );
};
